import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Users, 
  BookOpen, 
  TrendingUp,
  Calendar,
  Clock,
  Target,
  Save,
  X
} from 'lucide-react';
import axios from 'axios';

interface Exam {
  id: number;
  title: string;
  description: string;
  duration_minutes: number;
  max_attempts: number;
  deadline: string;
  total_questions: number;
  question_count: number;
  is_active: boolean;
  created_at: string;
}

interface Question {
  id: number;
  question_text: string;
  question_type: 'true_false' | 'single_choice' | 'multiple_choice';
  options: string[];
  correct_answer: any;
  points: number;
  order_index: number;
}

interface Stats {
  total_exams: number;
  total_questions: number;
  total_attempts: number;
  unique_students: number;
}

const AdminPanel: React.FC = () => {
  const [exams, setExams] = useState<Exam[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showExamForm, setShowExamForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [editingExam, setEditingExam] = useState<Exam | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);

  // Form states
  const [examForm, setExamForm] = useState({
    title: '',
    description: '',
    duration_minutes: 30,
    max_attempts: 1,
    deadline: '',
    total_questions: 0
  });

  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_type: 'single_choice' as 'true_false' | 'single_choice' | 'multiple_choice',
    options: ['', '', '', ''],
    correct_answer: '',
    points: 1,
    order_index: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [examsResponse, statsResponse] = await Promise.all([
        axios.get('/admin/exams'),
        axios.get('/admin/stats')
      ]);

      setExams(examsResponse.data.exams);
      setStats(statsResponse.data.stats);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError('Error cargando los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (examId: number) => {
    try {
      const response = await axios.get(`/admin/exams/${examId}/questions`);
      setQuestions(response.data.questions);
    } catch (error: any) {
      console.error('Error fetching questions:', error);
      setError('Error cargando las preguntas');
    }
  };

  const handleCreateExam = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post('/admin/exams', examForm);
      setExams([response.data.exam, ...exams]);
      setShowExamForm(false);
      setExamForm({
        title: '',
        description: '',
        duration_minutes: 30,
        max_attempts: 1,
        deadline: '',
        total_questions: 0
      });
    } catch (error: any) {
      console.error('Error creating exam:', error);
      setError('Error creando el examen');
    }
  };

  const handleUpdateExam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingExam) return;

    try {
      const response = await axios.put(`/admin/exams/${editingExam.id}`, examForm);
      setExams(exams.map(exam => 
        exam.id === editingExam.id ? response.data.exam : exam
      ));
      setEditingExam(null);
      setShowExamForm(false);
    } catch (error: any) {
      console.error('Error updating exam:', error);
      setError('Error actualizando el examen');
    }
  };

  const handleDeleteExam = async (examId: number) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este examen?')) return;

    try {
      await axios.delete(`/admin/exams/${examId}`);
      setExams(exams.filter(exam => exam.id !== examId));
    } catch (error: any) {
      console.error('Error deleting exam:', error);
      setError('Error eliminando el examen');
    }
  };

  const handleCreateQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedExam) return;

    try {
      const questionData = {
        ...questionForm,
        options: questionForm.options.filter(opt => opt.trim() !== ''),
        correct_answer: questionForm.question_type === 'true_false' 
          ? questionForm.correct_answer === 'true'
          : questionForm.question_type === 'single_choice'
          ? questionForm.correct_answer
          : questionForm.correct_answer.split(',').map(a => a.trim())
      };

      const response = await axios.post(`/admin/exams/${selectedExam.id}/questions`, questionData);
      setQuestions([...questions, response.data.question]);
      setShowQuestionForm(false);
      setQuestionForm({
        question_text: '',
        question_type: 'single_choice',
        options: ['', '', '', ''],
        correct_answer: '',
        points: 1,
        order_index: 0
      });
    } catch (error: any) {
      console.error('Error creating question:', error);
      setError('Error creando la pregunta');
    }
  };

  const handleDeleteQuestion = async (questionId: number) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta pregunta?')) return;

    try {
      await axios.delete(`/admin/questions/${questionId}`);
      setQuestions(questions.filter(q => q.id !== questionId));
    } catch (error: any) {
      console.error('Error deleting question:', error);
      setError('Error eliminando la pregunta');
    }
  };

  const openExamForm = (exam?: Exam) => {
    if (exam) {
      setEditingExam(exam);
      setExamForm({
        title: exam.title,
        description: exam.description,
        duration_minutes: exam.duration_minutes,
        max_attempts: exam.max_attempts,
        deadline: exam.deadline ? exam.deadline.split('T')[0] : '',
        total_questions: exam.total_questions
      });
    } else {
      setEditingExam(null);
      setExamForm({
        title: '',
        description: '',
        duration_minutes: 30,
        max_attempts: 1,
        deadline: '',
        total_questions: 0
      });
    }
    setShowExamForm(true);
  };

  const openQuestionForm = (exam: Exam) => {
    setSelectedExam(exam);
    setShowQuestionForm(true);
    fetchQuestions(exam.id);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: 'white', fontSize: '18px' }}>
          Cargando panel de administración... 👑
        </p>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="title"
      >
        <h1>
          <span className="emoji">👑</span>
          Panel de Administración
          <span className="emoji">🎯</span>
        </h1>
        <h2>Gestiona Exámenes y Preguntas</h2>
      </motion.div>

      {/* Estadísticas */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-4"
        >
          <div className="card" style={{ textAlign: 'center' }}>
            <BookOpen style={{ fontSize: '2rem', color: '#4ecdc4', marginBottom: '10px' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats.total_exams}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Exámenes</p>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <Target style={{ fontSize: '2rem', color: '#ff6b6b', marginBottom: '10px' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats.total_questions}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Preguntas</p>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <TrendingUp style={{ fontSize: '2rem', color: '#ffd700', marginBottom: '10px' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats.total_attempts}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Intentos</p>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <Users style={{ fontSize: '2rem', color: '#56ab2f', marginBottom: '10px' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats.unique_students}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Estudiantes</p>
          </div>
        </motion.div>
      )}

      {/* Botón para crear examen */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        style={{ textAlign: 'center', marginBottom: '30px' }}
      >
        <button
          onClick={() => openExamForm()}
          className="btn"
          style={{ fontSize: '18px', padding: '15px 30px' }}
        >
          <Plus style={{ marginRight: '8px' }} />
          Crear Nuevo Examen
        </button>
      </motion.div>

      {/* Lista de exámenes */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h3 style={{ color: 'white', marginBottom: '20px' }}>
          📚 Exámenes Existentes
        </h3>

        {exams.length === 0 ? (
          <div className="card" style={{ textAlign: 'center' }}>
            <h3>📝 No hay exámenes creados</h3>
            <p>¡Crea tu primer examen para empezar!</p>
          </div>
        ) : (
          <div className="grid grid-2">
            {exams.map((exam, index) => (
              <motion.div
                key={exam.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="card"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                  <h4 style={{ color: '#333', margin: 0 }}>{exam.title}</h4>
                  <span style={{ 
                    background: exam.is_active ? '#56ab2f' : '#ff416c', 
                    color: 'white', 
                    padding: '5px 10px', 
                    borderRadius: '15px', 
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {exam.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                <p style={{ color: '#666', marginBottom: '15px' }}>
                  {exam.description || 'Sin descripción'}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Clock style={{ color: '#4ecdc4', marginBottom: '5px' }} />
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{exam.duration_minutes} min</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Duración</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Target style={{ color: '#ff6b6b', marginBottom: '5px' }} />
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{exam.question_count}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Preguntas</div>
                  </div>
                </div>

                {exam.deadline && (
                  <div style={{ 
                    background: 'rgba(255, 107, 107, 0.1)', 
                    padding: '10px', 
                    borderRadius: '10px', 
                    marginBottom: '15px',
                    textAlign: 'center'
                  }}>
                    <Calendar style={{ display: 'inline', marginRight: '5px', color: '#ff6b6b' }} />
                    <span style={{ fontSize: '14px', color: '#ff6b6b', fontWeight: 'bold' }}>
                      {new Date(exam.deadline).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => openQuestionForm(exam)}
                    className="btn btn-secondary"
                    style={{ flex: 1, minWidth: '120px' }}
                  >
                    <Eye style={{ marginRight: '5px' }} />
                    Preguntas
                  </button>
                  <button
                    onClick={() => openExamForm(exam)}
                    className="btn btn-secondary"
                    style={{ flex: 1, minWidth: '120px' }}
                  >
                    <Edit style={{ marginRight: '5px' }} />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDeleteExam(exam.id)}
                    className="btn btn-danger"
                    style={{ flex: 1, minWidth: '120px' }}
                  >
                    <Trash2 style={{ marginRight: '5px' }} />
                    Eliminar
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Modal para crear/editar examen */}
      {showExamForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="card"
          style={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            zIndex: 1000,
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{editingExam ? 'Editar Examen' : 'Crear Nuevo Examen'}</h3>
            <button
              onClick={() => setShowExamForm(false)}
              className="btn btn-danger"
              style={{ padding: '5px 10px' }}
            >
              <X />
            </button>
          </div>

          <form onSubmit={editingExam ? handleUpdateExam : handleCreateExam}>
            <div className="input-group">
              <label>Título del Examen</label>
              <input
                type="text"
                value={examForm.title}
                onChange={(e) => setExamForm({...examForm, title: e.target.value})}
                required
                placeholder="Ej: Examen de Frutas en Inglés"
              />
            </div>

            <div className="input-group">
              <label>Descripción</label>
              <textarea
                value={examForm.description}
                onChange={(e) => setExamForm({...examForm, description: e.target.value})}
                placeholder="Describe el examen..."
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  border: '3px solid #ff6b6b',
                  borderRadius: '25px',
                  fontFamily: 'Comic Sans MS, cursive',
                  fontSize: '16px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: '100px'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="input-group">
                <label>Duración (minutos)</label>
                <input
                  type="number"
                  value={examForm.duration_minutes}
                  onChange={(e) => setExamForm({...examForm, duration_minutes: parseInt(e.target.value)})}
                  required
                  min="1"
                />
              </div>

              <div className="input-group">
                <label>Intentos Permitidos</label>
                <input
                  type="number"
                  value={examForm.max_attempts}
                  onChange={(e) => setExamForm({...examForm, max_attempts: parseInt(e.target.value)})}
                  required
                  min="1"
                />
              </div>
            </div>

            <div className="input-group">
              <label>Fecha Límite (opcional)</label>
              <input
                type="datetime-local"
                value={examForm.deadline}
                onChange={(e) => setExamForm({...examForm, deadline: e.target.value})}
              />
            </div>

            <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
              <button type="submit" className="btn">
                <Save style={{ marginRight: '8px' }} />
                {editingExam ? 'Actualizar' : 'Crear'}
              </button>
              <button
                type="button"
                onClick={() => setShowExamForm(false)}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Modal para gestionar preguntas */}
      {showQuestionForm && selectedExam && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="card"
          style={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            zIndex: 1000,
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>Preguntas de: {selectedExam.title}</h3>
            <button
              onClick={() => setShowQuestionForm(false)}
              className="btn btn-danger"
              style={{ padding: '5px 10px' }}
            >
              <X />
            </button>
          </div>

          {/* Lista de preguntas existentes */}
          <div style={{ marginBottom: '30px' }}>
            <h4>Preguntas Existentes ({questions.length})</h4>
            {questions.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                No hay preguntas en este examen
              </p>
            ) : (
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                {questions.map((question, index) => (
                  <div key={question.id} style={{ 
                    background: 'rgba(0,0,0,0.05)', 
                    padding: '15px', 
                    borderRadius: '10px', 
                    marginBottom: '10px' 
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <strong>{index + 1}. {question.question_text}</strong>
                        <p style={{ color: '#666', fontSize: '14px', margin: '5px 0' }}>
                          Tipo: {question.question_type === 'true_false' ? 'Verdadero/Falso' :
                                question.question_type === 'single_choice' ? 'Opción Única' : 'Opción Múltiple'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteQuestion(question.id)}
                        className="btn btn-danger"
                        style={{ padding: '5px 10px', marginLeft: '10px' }}
                      >
                        <Trash2 />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Formulario para nueva pregunta */}
          <form onSubmit={handleCreateQuestion}>
            <h4>Agregar Nueva Pregunta</h4>
            
            <div className="input-group">
              <label>Texto de la Pregunta</label>
              <textarea
                value={questionForm.question_text}
                onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                required
                placeholder="Escribe la pregunta aquí..."
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  border: '3px solid #ff6b6b',
                  borderRadius: '25px',
                  fontFamily: 'Comic Sans MS, cursive',
                  fontSize: '16px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>

            <div className="input-group">
              <label>Tipo de Pregunta</label>
              <select
                value={questionForm.question_type}
                onChange={(e) => setQuestionForm({...questionForm, question_type: e.target.value as any})}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  border: '3px solid #ff6b6b',
                  borderRadius: '25px',
                  fontFamily: 'Comic Sans MS, cursive',
                  fontSize: '16px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  outline: 'none'
                }}
              >
                <option value="single_choice">Opción Única</option>
                <option value="multiple_choice">Opción Múltiple</option>
                <option value="true_false">Verdadero/Falso</option>
              </select>
            </div>

            {questionForm.question_type !== 'true_false' && (
              <div className="input-group">
                <label>Opciones de Respuesta</label>
                {questionForm.options.map((option, index) => (
                  <input
                    key={index}
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...questionForm.options];
                      newOptions[index] = e.target.value;
                      setQuestionForm({...questionForm, options: newOptions});
                    }}
                    placeholder={`Opción ${index + 1}`}
                    style={{ marginBottom: '10px' }}
                  />
                ))}
              </div>
            )}

            <div className="input-group">
              <label>Respuesta Correcta</label>
              {questionForm.question_type === 'true_false' ? (
                <select
                  value={questionForm.correct_answer}
                  onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '15px 20px',
                    border: '3px solid #ff6b6b',
                    borderRadius: '25px',
                    fontFamily: 'Comic Sans MS, cursive',
                    fontSize: '16px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    outline: 'none'
                  }}
                >
                  <option value="">Selecciona...</option>
                  <option value="true">Verdadero</option>
                  <option value="false">Falso</option>
                </select>
              ) : questionForm.question_type === 'single_choice' ? (
                <select
                  value={questionForm.correct_answer}
                  onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '15px 20px',
                    border: '3px solid #ff6b6b',
                    borderRadius: '25px',
                    fontFamily: 'Comic Sans MS, cursive',
                    fontSize: '16px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    outline: 'none'
                  }}
                >
                  <option value="">Selecciona la respuesta correcta...</option>
                  {questionForm.options.map((option, index) => (
                    <option key={index} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={questionForm.correct_answer}
                  onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}
                  placeholder="Separa múltiples respuestas con comas"
                  style={{
                    width: '100%',
                    padding: '15px 20px',
                    border: '3px solid #ff6b6b',
                    borderRadius: '25px',
                    fontFamily: 'Comic Sans MS, cursive',
                    fontSize: '16px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    outline: 'none'
                  }}
                />
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="input-group">
                <label>Puntos</label>
                <input
                  type="number"
                  value={questionForm.points}
                  onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})}
                  min="1"
                />
              </div>

              <div className="input-group">
                <label>Orden</label>
                <input
                  type="number"
                  value={questionForm.order_index}
                  onChange={(e) => setQuestionForm({...questionForm, order_index: parseInt(e.target.value)})}
                  min="0"
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
              <button type="submit" className="btn">
                <Plus style={{ marginRight: '8px' }} />
                Agregar Pregunta
              </button>
              <button
                type="button"
                onClick={() => setShowQuestionForm(false)}
                className="btn btn-secondary"
              >
                Cerrar
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginTop: '20px' }}>
          😔 {error}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
